import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http, BACKEND_URL } from "../lib/api";

const BrandCtx = createContext(null);

const DEFAULT_BRAND = {
  display_name: "GlowBit Central",
  colors: { primary: "#B96F86", dark: "#512A46", bg: "#F8EEF1", accent: "#F3ABA4" },
  logo_url: "/glowbit-logo.webp",
  has_custom_logo: false,
};

function absolutize(url) {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url; // static assets served by frontend (e.g. /glowbit-logo.png)
}

function applyBrand(brand) {
  const root = document.documentElement;
  const c = brand.colors || {};
  if (c.primary) root.style.setProperty("--brand-primary", c.primary);
  if (c.dark) root.style.setProperty("--brand-dark", c.dark);
  if (c.bg) root.style.setProperty("--brand-bg", c.bg);
  if (c.accent) root.style.setProperty("--brand-accent", c.accent);
  if (c.primary) root.style.setProperty("--accent-green", c.primary);
  if (brand.display_name) document.title = brand.display_name;
}

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULT_BRAND);

  const refreshBrand = useCallback(async () => {
    try {
      const { data } = await http.get("/brand");
      const next = { ...data, logo_url: data.has_custom_logo ? absolutize(data.logo_url) : DEFAULT_BRAND.logo_url };
      setBrand(next);
      applyBrand(next);
      return next;
    } catch {
      applyBrand(DEFAULT_BRAND);
      return DEFAULT_BRAND;
    }
  }, []);

  useEffect(() => { refreshBrand(); }, [refreshBrand]);

  return (
    <BrandCtx.Provider value={{ brand, refreshBrand, setBrand }}>
      {children}
    </BrandCtx.Provider>
  );
}

export const useBrand = () => useContext(BrandCtx) || { brand: DEFAULT_BRAND, refreshBrand: async () => {} };
